import Radio from '@/modules/drivers/shared/Radio'
import { ContractDetailsModel } from '@/modules/drivers/types/contracts'
import * as React from 'react'
import RateReduce from './RateReduce'
import RateReduceCustom from './RateReduceCustom'
import NumberFieldInnerTable from '../../inputs/NumberFieldInnerTable'
import { useMinimumWageQuery } from '@/modules/settings/pages/minimumWage/useMinimumWageQuery'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { PolishMinimumWageConfigurationExpandableItemModel } from '@/app/models/configuration/minimumWage/polishMinimumWageConfigurationModel'
import moment from 'moment'

// Jak DriverContractToRateSummaryConverter w WPF: data końca (albo dziś), nie wcześniej niż początek.
const getMinimalRateForContract = (
  items: PolishMinimumWageConfigurationExpandableItemModel[] | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
) => {
  let checkDate = endDate ? moment(endDate) : moment()
  if (startDate && checkDate.isBefore(moment(startDate), 'day')) {
    checkDate = moment(startDate)
  }
  return [...(items ?? [])]
    .sort((a, b) => moment(b.rateDate).valueOf() - moment(a.rateDate).valueOf())
    .find((x) => !moment(x.rateDate).isAfter(checkDate, 'day'))?.rateValue
}

interface Props {
  data: ContractDetailsModel | null
  setData: React.Dispatch<React.SetStateAction<ContractDetailsModel | null>>
  companyId: string
}

const RateOptions = ({ data, setData, companyId }: Props) => {
  const { t } = useLocalize()
  const isMonthlySettlement =
    data?.settlement === 'Month' || data?.settlement === 'ForContract'

  const { data: minialWage } = useMinimumWageQuery(companyId)

  const polishWage = getMinimalRateForContract(
    isMonthlySettlement
      ? minialWage?.polishConfiguration?.expandableItems
      : minialWage?.polishConfiguration?.hourRateExpandableItems,
    data?.startDate,
    data?.endDate,
  )

  React.useEffect(() => {
    if (data?.contractRate === undefined) {
      if (
        polishWage &&
        data?.isMinimalContractRate &&
        data.contractRate !== Number(polishWage)
      ) {
        setData((prev) =>
          prev ? { ...prev, contractRate: Number(polishWage) } : prev,
        )
      } else if (
        polishWage &&
        data?.isMinimalContractRate === false &&
        data.contractRate === Number(polishWage)
      ) {
        setData((prev) => (prev ? { ...prev, contractRate: undefined } : prev))
      }
    }
  }, [polishWage, data?.isMinimalContractRate])

  // Zmiana dat przenosi umowę do innego wiersza stawek — podmieniamy tylko wartość wpisaną automatycznie.
  const prevPolishWage = React.useRef(polishWage)
  React.useEffect(() => {
    const prev = prevPolishWage.current
    prevPolishWage.current = polishWage
    if (
      prev !== undefined &&
      polishWage !== undefined &&
      prev !== polishWage &&
      data?.isMinimalContractRate &&
      data.contractRate === Number(prev)
    ) {
      setData((p) => (p ? { ...p, contractRate: Number(polishWage) } : p))
    }
  }, [polishWage])

  React.useEffect(() => {
    if (data?.isMinimalContractRate === null || data?.isMinimalContractRate === undefined ) {
      setData((prev) =>
        prev ? { ...prev, isMinimalContractRate: true } : prev,
      )
    }
  },[])

  const prevSettlement = React.useRef(data?.settlement)
  React.useEffect(() => {
    if (prevSettlement.current === data?.settlement) return
    const wasMonthly =
      prevSettlement.current === 'Month' ||
      prevSettlement.current === 'ForContract'
    prevSettlement.current = data?.settlement
    if (wasMonthly && isMonthlySettlement) return

    const nextRate =
      data?.isMinimalContractRate && polishWage
        ? Number(polishWage)
        : data?.settlement === 'Hour'
          ? 0
          : undefined

    setData((prev) => (prev ? { ...prev, contractRate: nextRate } : prev))
  }, [data?.settlement])

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-bg-[#f2f2f2]/50 tw-px-4 tw-py-2">
      <p className="tw-text-sm tw-font-medium tw-text-content-base">Stawka</p>
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Radio
            checked={data?.isMinimalContractRate === false}
            onChange={(e) => setData({ ...data, isMinimalContractRate: !e })}
          />
          <p className="tw-text-sm tw-text-content-base">
            {isMonthlySettlement ? 'Stawka zasadnicza' : 'Stawka godzinowa'}
          </p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Radio
            checked={data?.isMinimalContractRate}
            onChange={(e) => setData({ ...data, isMinimalContractRate: e })}
          />
          <p className="tw-text-sm tw-text-content-base">Stawka minimalna</p>
        </div>
      </div>
      {data?.isMinimalContractRate !== undefined && (
        <NumberFieldInnerTable
          inputSize="big"
          fullWidth
          innerLabel="PLN"
          placeholder="0,00"
          decimalPlaces={2}
          value={data?.contractRate?.toString() || ''}
          onChange={(e) => {
            setData({ ...data, contractRate: Number(e.target.value) })
          }}
        />
      )}
      {/* Pomniejszanie samej zasadniczej - w WPF osobny checkbox przy polu "Stawka
          zasadnicza" (ReduceMonthRate) + sekcja "*Pomniejszaj:". Tylko dla rozliczenia
          miesięcznego, bo przy stawce godzinowej podstawa nie jest pomniejszana. */}
      {data?.type !== 'CivilLawAgreement' && isMonthlySettlement && (
        <RateReduceCustom
          data={data}
          setData={setData}
          reduceType="rate"
          padding={false}
          disabled={data?.contractRate === undefined || data?.contractRate <= 0}
          mainCheckboxProperty="reduceMonthRate"
          mainLabel={t('contract.reduce-month-rate-on-absence')}
          sickLeaveProperty="reduceContractDataForSickLeave"
          paidLeaveProperty="reduceContractDataForLeave"
          unpaidLeaveProperty="reduceContractDataForNotPaidLeave"
          sickLeaveLabel={t('contract.sick-leave-1-30')}
          paidLeaveLabel={t('contract.paid-leave-by-dimension')}
          unpaidLeaveLabel={t('contract.unpaid-leave-by-dimension')}
        />
      )}
      {data?.type !== 'CivilLawAgreement' && (
        <RateReduce
          data={data}
          disabled={data?.contractRate === undefined || data?.contractRate <= 0}
          setData={setData}
        />
      )}
    </div>
  )
}

export default RateOptions
